import type { ReactNode } from "react";
import {
  type Identifier,
  useGetList,
  useGetOne,
  useRecordContext,
  useTranslate,
} from "ra-core";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";

import { isBeforeFriday } from "../tasks/tasksPredicate";
import type { Contact, Task as TaskRecord } from "../types";
import { contactTasksPath } from "./paths";
import { TaskRows } from "./TaskRows";
import {
  bucketByDueDate,
  CLOSED_TASKS_FETCHED,
  contactPanelTasks,
  DUE_SECTIONS,
  partitionTasks,
} from "./taskLists";

/**
 * A contact's tasks, on two screens with two different jobs.
 *
 *   ContactTasksPanel    on the contact page -- what is outstanding, now
 *   ContactTaskHistory   at /contacts/:id/tasks -- everything, ever
 *
 * WHY THE PANEL GOT SMALLER RATHER THAN BIGGER
 * --------------------------------------------
 * Asked for in the owner's own words: "at the current contact detail page, I
 * dont need to see everything -- just the currently open tasks and, if there
 * arent any open ones, a couple of the last closed ones."
 *
 * The panel sits on a record that also carries notes, a touch log, a lifecycle
 * and a company, so every row it adds pushes the thing you opened the page for
 * further down. It answers one question -- what is outstanding on this person
 * -- and hands the other question to a page with room for it. That is the same
 * trade TouchHistory made, and the "See all" link below is the same link.
 *
 * WHY THE READ IS TWO QUERIES AND NOT ONE
 * ---------------------------------------
 * Upstream reads every task the contact has ever had (`perPage: 1000`, no
 * done-predicate) and decides done-ness in the browser. That works, and it has
 * one cost that is invisible from the screen: a partial index predicated on
 * `done_date is null` cannot serve a query that does not carry that predicate,
 * so migration 089's `tasks_open_by_contact` sat unused by the one page most
 * likely to need it (crm#27).
 *
 * Splitting the read fixes that and is also just less data: the open half
 * carries `done_date is null` and matches the index exactly, and the closed
 * half is `order by done_date desc limit 10` rather than everything.
 *
 * THE CLOSED HALF IS NOT OPTIONAL, EVEN THOUGH IT USUALLY GOES UNSHOWN. It
 * carries the five-minute grace: a task ticked thirty seconds ago now arrives
 * on the closed side, and without that query it would vanish from under the
 * person who just ticked it. The owner asked for that behavior by name.
 */

/** How many open tasks the panel will read. One contact, so this is a ceiling. */
const OPEN_TASKS_FETCHED = 100;

const panelQuery = (contactId: Identifier) => ({
  open: {
    filter: { contact_id: contactId, "done_date@is": null },
    sort: { field: "due_date", order: "ASC" as const },
    pagination: { page: 1, perPage: OPEN_TASKS_FETCHED },
  },
  closed: {
    filter: { contact_id: contactId, "done_date@not.is": null },
    // NEWEST FIRST, and `contactPanelTasks` depends on it -- it takes the
    // first couple without re-sorting, so an ascending sort here would show
    // the two OLDEST closed tasks under a heading that says the opposite.
    sort: { field: "done_date", order: "DESC" as const },
    pagination: { page: 1, perPage: CLOSED_TASKS_FETCHED },
  },
});

/**
 * The contact page's task panel.
 *
 * Rendered in BOTH halves of contacts/ContactShow.tsx -- the aside on desktop
 * and the Tasks tab on a phone. That file forks into two whole
 * implementations, and check-injections.mjs counts this tag in each.
 */
export const ContactTasksPanel = ({
  emptyAction,
}: {
  /**
   * What to offer when this contact has never had a task. The desktop aside
   * renders `<AddTask />` permanently below the panel and passes nothing; the
   * mobile tab has no such control and passes one, which is what upstream's
   * empty placeholder did before this panel replaced it.
   */
  emptyAction?: ReactNode;
}) => {
  const contact = useRecordContext<Contact>();
  const contactId = contact?.id;
  const query = panelQuery(contactId ?? 0);

  const {
    data: open,
    total: openTotal,
    isPending: openPending,
    error: openError,
  } = useGetList<TaskRecord>("tasks", query.open, {
    enabled: contactId != null,
  });

  const {
    data: closed,
    total: closedTotal,
    isPending: closedPending,
    error: closedError,
  } = useGetList<TaskRecord>("tasks", query.closed, {
    enabled: contactId != null,
  });

  if (!contact) return null;

  const isPending = openPending || closedPending;
  const error = openError ?? closedError;

  /*
   * A FAILED READ MUST NOT LOOK LIKE AN EMPTY LIST. "No tasks" on a contact
   * whose read was denied is a confident false statement about whether anybody
   * is working them -- and this panel feeds the same judgement the follow-up
   * queues make. Say which one it is.
   */
  if (error)
    return (
      <p className="text-sm text-destructive">
        Could not load this contact&apos;s tasks, so this is not a report that
        nothing is outstanding — it is a report of not knowing.
      </p>
    );

  if (isPending)
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 2 }).map((_, index) => (
          <Skeleton className="w-full h-8" key={index} />
        ))}
      </div>
    );

  const { rows, mode } = contactPanelTasks(open, closed);
  const total = (openTotal ?? 0) + (closedTotal ?? 0);

  return (
    <div className="flex flex-col gap-3">
      {mode === "open" && <OpenSections tasks={rows} />}

      {mode === "fallback" && (
        <div className="flex flex-col gap-2">
          {/*
            THE SENTENCE IS THE POINT, not the rows under it. An empty panel
            and a panel showing closed work read the same way without it --
            "there is nothing here" versus "there is nothing left to do here,
            and this is what was done".
          */}
          <p className="text-xs text-muted-foreground">
            Nothing open. The last{" "}
            {rows.length === 1 ? "task" : `${rows.length} tasks`} closed:
          </p>
          <TaskRows tasks={rows} />
        </div>
      )}

      {mode === "empty" && (
        <div className="flex flex-col items-start gap-2">
          <p className="text-sm text-muted-foreground">
            No tasks, open or closed. Nobody has planned a next step for this
            contact.
          </p>
          {emptyAction}
        </div>
      )}

      {/*
        THE LINK APPEARS ONLY WHEN SOMETHING IS ACTUALLY HIDDEN, which is the
        same rule TouchHistory uses. "See all 2 tasks" on a panel already
        showing both of them is a link that teaches the reader it is not worth
        clicking, and they then do not click it on the contact where it
        matters.
      */}
      {total > rows.length && (
        <div>
          <Button
            asChild
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs text-muted-foreground underline hover:no-underline"
          >
            <Link to={contactTasksPath(contact.id)}>
              See all {total} tasks, open and closed
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
};

/**
 * Every task for one contact, open and closed, on its own page.
 *
 * Reached from the panel above, and from the lifecycle screen's link to the
 * task a step wrote. A separate route rather than an expanding panel for the
 * reasons CONTACT_TASKS_PATH records: a long history deserves its own scroll
 * position, its own back button, and a URL somebody can send to someone else.
 *
 * THE COMPLETED HALF IS THE WHOLE POINT (atomic#15). Until this page existed a
 * task closed more than five minutes ago was invisible on every surface in the
 * app -- the row was in the database with its `done_date` and nothing could
 * display it. `enrollment_steps` records that work was ASKED for; the closed
 * task is the only record that it was DONE.
 */
export const ContactTaskHistory = () => {
  const { id } = useParams();

  const { data: contact } = useGetOne<Contact>(
    "contacts",
    { id: id! },
    { enabled: !!id },
  );

  const { data, total, isPending, error } = useGetList<TaskRecord>(
    "tasks",
    {
      filter: { contact_id: id ?? 0 },
      // DUE DATE, not done date: the open half is a plan and reads in the
      // order it has to be worked. The completed half is re-sorted below.
      sort: { field: "due_date", order: "ASC" },
      pagination: { page: 1, perPage: 500 },
    },
    { enabled: !!id },
  );

  const name = contact
    ? `${contact.first_name} ${contact.last_name}`.trim()
    : "this contact";

  const { open, completed } = partitionTasks(data);

  return (
    <div className="max-w-3xl">
      <Button
        asChild
        variant="ghost"
        size="sm"
        className="-ml-2 mb-2 text-muted-foreground"
      >
        <Link to={id ? `/contacts/${id}/show` : "/contacts"}>
          <ArrowLeft className="size-4" /> Back to {name}
        </Link>
      </Button>

      <h1 className="text-xl font-semibold">Tasks</h1>
      <p className="text-sm text-muted-foreground">
        Everything ever asked of {name}
        {typeof total === "number" ? ` — ${total} in total` : ""}. The contact
        page shows only what is still outstanding; this is the record of what
        was done.
      </p>

      {error ? (
        <p className="mt-4 text-sm text-destructive">
          Could not load these tasks, so this is not a record of nothing having
          been asked — it is a record of not knowing.
        </p>
      ) : isPending ? (
        <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="mt-4 flex flex-col gap-6">
          <HistorySection
            title="Open"
            tasks={open}
            empty="Nothing is outstanding for this contact."
          />
          <HistorySection
            title="Completed"
            /*
              NEWEST FIRST. The list arrived sorted by due date, which is the
              right order for work still to be done and the wrong one for work
              already finished -- "what happened here lately" is the question
              this half answers, and a task due last year but closed this
              morning is the most recent thing that happened.
            */
            tasks={[...completed].sort(byDoneDateDesc)}
            empty="Nothing has been completed for this contact yet."
          />
        </div>
      )}
    </div>
  );
};

/** Newest completion first. `done_date` is set on every row in this list. */
const byDoneDateDesc = (a: TaskRecord, b: TaskRecord) =>
  (b.done_date ?? "").localeCompare(a.done_date ?? "");

const HistorySection = ({
  title,
  tasks,
  empty,
}: {
  title: string;
  tasks: TaskRecord[];
  empty: string;
}) => (
  <div>
    <div className="flex items-baseline justify-between gap-2">
      <h2 className="text-sm font-semibold">{title}</h2>
      <span className="text-xs text-muted-foreground">{tasks.length}</span>
    </div>
    <Separator className="mt-1.5 mb-3" />
    {tasks.length ? (
      <TaskRows tasks={tasks} />
    ) : (
      <p className="text-sm text-muted-foreground">{empty}</p>
    )}
  </div>
);

/**
 * The panel's open work, in upstream's own due-date sections.
 *
 * NOT `TasksIterator`, WHICH IS WHAT THE ROWS USED TO GO THROUGH. That
 * component re-applies `!isDone || isRecentlyDone` to whatever it is handed,
 * which is right where it sits and would silently empty the closed fallback
 * below -- rows fetched, selected and then dropped by the renderer, with
 * nothing on screen to say so.
 */
const OpenSections = ({ tasks }: { tasks: TaskRecord[] }) => {
  const translate = useTranslate();
  const sections = bucketByDueDate(tasks);

  return (
    <div className="flex flex-col gap-4">
      {DUE_SECTIONS.map((section) => {
        const rows = sections[section.key];
        if (!rows.length) return null;
        // Upstream's own rule for the contact panel: from Friday on, "this
        // week" holds a day or two and reads as an empty heading.
        if ("thisWeekBeforeFridayOnly" in section && !isBeforeFriday())
          return null;

        return (
          <div className="flex flex-col gap-2" key={section.key}>
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">
              {translate(section.titleKey)}
            </p>
            <TaskRows tasks={rows} />
          </div>
        );
      })}
    </div>
  );
};
